import * as React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PropsWithChildren, Suspense } from "react";
import CenterSpinner from "../sharedComponents/CenterSpinner";
import { useIsRestoring } from "@tanstack/react-query";
import useAuth from "../store/auth/authState";
import { isAdminFromToken } from "./auth/utils/jwt";
import { SidebarCountsProvider } from "../components/providers/SidebarCountsProvider";
const VideosPage = React.lazy(() => import("./videos/components/VideosPage"));
const AudiosPage = React.lazy(() => import("./audios/components/AudiosPage"));
const OnboardingPage = React.lazy(
  () => import("./onboarding/components/OnboardingPage"),
);
const MarketplaceListingsPage = React.lazy(
  () => import("./marketplace/pages/MarketplaceListings.page"),
);
const MarketplaceDisciplinesPage = React.lazy(
  () => import("./marketplace/pages/MarketplaceDisciplines.page"),
);
const MarketplacePacksPage = React.lazy(
  () => import("./marketplace/pages/MarketplacePacks.page"),
);
const AssetPipelineRunsPage = React.lazy(
  () => import("./assetPipeline/pages/AssetPipelineRuns.page"),
);
const AssetPipelineRunPage = React.lazy(
  () => import("./assetPipeline/pages/AssetPipelineRun.page"),
);
const MagicRevealLevelsPage = React.lazy(
  () => import("./games/pages/MagicRevealLevels.page"),
);
const MagicRevealBackgroundsPage = React.lazy(
  () => import("./games/pages/MagicRevealBackgrounds.page"),
);
const ArticlesPage = React.lazy(() => import("./articles/pages/Articles.page"));
const ArticleEditorPage = React.lazy(
  () => import("./articles/pages/ArticleEditor.page"),
);
const ArticleLinkHealthPage = React.lazy(
  () => import("./articles/pages/ArticleLinkHealth.page"),
);
const CrmLeadsPage = React.lazy(() => import("./crm/pages/CrmLeads.page"));
const CrmPipelinePage = React.lazy(
  () => import("./crm/pages/CrmPipeline.page"),
);
const CrmTasksPage = React.lazy(() => import("./crm/pages/CrmTasks.page"));
const CrmInsightsPage = React.lazy(
  () => import("./crm/pages/CrmInsights.page"),
);
const CrmSpendPage = React.lazy(() => import("./crm/pages/CrmSpend.page"));
const BillingSubscribersPage = React.lazy(
  () => import("./subscriptions/pages/BillingSubscribers.page"),
);
const BillingPlansPage = React.lazy(
  () => import("./subscriptions/pages/BillingPlans.page"),
);
const BillingFeaturesPage = React.lazy(
  () => import("./subscriptions/pages/BillingFeatures.page"),
);
const BillingEarlyAccessPage = React.lazy(
  () => import("./subscriptions/pages/BillingEarlyAccess.page"),
);
const BillingOffersPage = React.lazy(
  () => import("./subscriptions/pages/BillingOffers.page"),
);
const AdminUsersPage = React.lazy(
  () => import("./adminUsers/pages/AdminUsers.page"),
);
const AcceptInvitationPage = React.lazy(
  () => import("./adminUsers/pages/AcceptInvitation.page"),
);
const GoogleCallbackPage = React.lazy(
  () => import("./auth/components/GoogleCallback.page"),
);
const NotAuthorizedPage = React.lazy(
  () => import("./auth/components/NotAuthorized.page"),
);

const LazyComponent = (Component: React.LazyExoticComponent<any>) => {
  return (
    <Suspense fallback={<CenterSpinner />}>
      <Component />
    </Suspense>
  );
};

const GuardedRoute = ({
  children,
  requireAdmin = false,
}: PropsWithChildren<{ requireAdmin?: boolean }>) => {
  const { tokens } = useAuth();
  const isRestoring = useIsRestoring();
  const location = useLocation();
  if (isRestoring) return <CenterSpinner />;
  if (!tokens.accessToken) {
    // Carry a genuine deep link through sign-in so it survives the bounce (the
    // invitation accept link is the one that needs this). The bare root is NOT
    // a deep link — forwarding it would change where every admin lands after a
    // normal sign-in, which is `/categories`.
    //
    // `location.hash` is deliberately dropped: no admin route carries state in
    // a fragment, and a fragment is the one part of a URL that a redirect
    // allowlist cannot meaningfully validate. Add it here if that ever changes.
    const target = `${location.pathname}${location.search}`;
    const isDeepLink = location.pathname !== "/";
    const suffix = isDeepLink ? `?next=${encodeURIComponent(target)}` : "";
    return <Navigate to={`/auth/sign-in${suffix}`} replace />;
  }
  // Defence-in-depth (S4b): the admin shell requires the `admin` claim, not just
  // a valid token. A signed-in non-admin (e.g. a Google identity that
  // authenticated but was never granted access) lands on the terminal
  // not-authorized page instead of the shell — which would otherwise fire
  // admin-only calls and paint 403 noise. NOT applied to the invitation-accept
  // route, whose invitee legitimately has no admin claim until they accept.
  if (requireAdmin && !isAdminFromToken(tokens.accessToken)) {
    return <Navigate to="/auth/not-authorized" replace />;
  }
  return <>{children}</>;
};

const Router = () => {
  const MainLayout = React.lazy(() => import("./main/components/MainLayout"));
  const MainDashboard = React.lazy(
    () => import("./main/components/MainDashboard"),
  );
  const Login = React.lazy(() => import("./auth/components/Login.page"));
  const CategoriesPage = React.lazy(
    () => import("./categories/components/CategoriesGrid.page"),
  );
  const ImageSection = React.lazy(
    () => import("./images/components/ImageSection"),
  );
  const LogoutComponent = React.lazy(
    () => import("./accountAndSettings/components/logoutComponent"),
  );

  const UsersManagement = React.lazy(() =>
    import("./main/components/UsersManagement").catch(
      () => import("../sharedComponents/PlaceholderComponent"),
    ),
  );
  const AdminSettings = React.lazy(() =>
    import("./main/components/AdminSettings").catch(
      () => import("../sharedComponents/PlaceholderComponent"),
    ),
  );
  const Analytics = React.lazy(() =>
    import("./main/components/Analytics").catch(
      () => import("../sharedComponents/PlaceholderComponent"),
    ),
  );

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/auth/sign-in" element={LazyComponent(Login)} />
      {/* Google sign-in bounce: exchanges the relayed code, then lands on `next`. */}
      <Route
        path="/auth/google/callback"
        element={LazyComponent(GoogleCallbackPage)}
      />
      {/* Terminal page for a signed-in identity without the admin claim. */}
      <Route
        path="/auth/not-authorized"
        element={LazyComponent(NotAuthorizedPage)}
      />

      {/*
        Invitation accept — PUBLIC (spec ADI D8): a new invitee has no session
        yet, so the page itself offers the sign-in that accepts the invitation.
        Deliberately OUTSIDE the admin layout: MainLayout + SidebarCountsProvider
        would fire admin-only calls and paint 403 noise over the one action the
        invitee came here to take.
      */}
      <Route
        path="/admin/invitations/accept"
        element={LazyComponent(AcceptInvitationPage)}
      />

      {/* Protected Admin Routes with Layout */}
      <Route
        path="/"
        element={
          <GuardedRoute requireAdmin>
            <SidebarCountsProvider>
              {LazyComponent(MainLayout)}
            </SidebarCountsProvider>
          </GuardedRoute>
        }
      >
        {/* Dashboard - default route */}
        <Route index element={LazyComponent(MainDashboard)} />

        {/* Content Management - your existing pages */}
        <Route path="categories" element={LazyComponent(CategoriesPage)} />
        <Route path="images" element={LazyComponent(ImageSection)} />
        <Route path="videos" element={LazyComponent(VideosPage)} />
        <Route path="audios" element={LazyComponent(AudiosPage)} />
        <Route path="onboarding" element={LazyComponent(OnboardingPage)} />

        {/*
          SEO D19 — knowledge-base articles. `articles/new` is the editor in
          create mode (the same `:articleId` route, so creating an article and
          landing on its id keeps the editor mounted); `link-health` is a
          static segment and outranks the param.
        */}
        <Route path="articles" element={LazyComponent(ArticlesPage)} />
        <Route
          path="articles/link-health"
          element={LazyComponent(ArticleLinkHealthPage)}
        />
        <Route
          path="articles/:articleId"
          element={LazyComponent(ArticleEditorPage)}
        />

        {/* AST-P2 asset generation pipeline: topic in, reviewed cards out. */}
        <Route
          path="asset-pipeline"
          element={LazyComponent(AssetPipelineRunsPage)}
        />
        <Route
          path="asset-pipeline/:runId"
          element={LazyComponent(AssetPipelineRunPage)}
        />

        {/* Marketplace (Phase 1) */}
        <Route
          path="marketplace/listings"
          element={LazyComponent(MarketplaceListingsPage)}
        />
        <Route
          path="marketplace/disciplines"
          element={LazyComponent(MarketplaceDisciplinesPage)}
        />
        <Route
          path="marketplace/packs"
          element={LazyComponent(MarketplacePacksPage)}
        />

        {/*
          Games — Magic Reveal admin levels (SDD 2026-09-18). `:levelId` is
          declared ahead of Task 8's editor so the route is never dead (see
          nav-integrity.test.ts); it currently renders the same list page, since
          nothing reads the param yet.
        */}
        <Route
          path="games/magic-reveal"
          element={LazyComponent(MagicRevealLevelsPage)}
        />
        {/* Explicit static route, declared before `:levelId` (spec MRB, AC4b):
            otherwise "backgrounds" would be read as a level id. */}
        <Route
          path="games/magic-reveal/backgrounds"
          element={LazyComponent(MagicRevealBackgroundsPage)}
        />
        <Route
          path="games/magic-reveal/:levelId"
          element={LazyComponent(MagicRevealLevelsPage)}
        />

        {/* CRM (potential clients + revenue/insights + absorbed subscriptions) */}
        <Route path="crm" element={LazyComponent(CrmLeadsPage)} />
        <Route path="crm/pipeline" element={LazyComponent(CrmPipelinePage)} />
        <Route path="crm/tasks" element={LazyComponent(CrmTasksPage)} />
        <Route path="crm/insights" element={LazyComponent(CrmInsightsPage)} />
        <Route path="crm/spend" element={LazyComponent(CrmSpendPage)} />
        {/*
          ADM D2 — the four Billing views are routes, not tabs. Each is
          linkable, bookmarkable, and survives a refresh; none of them could
          when they lived inside one page's tab strip.
        */}
        <Route
          path="billing/subscribers"
          element={LazyComponent(BillingSubscribersPage)}
        />
        <Route path="billing/plans" element={LazyComponent(BillingPlansPage)} />
        <Route
          path="billing/features"
          element={LazyComponent(BillingFeaturesPage)}
        />
        <Route
          path="billing/early-access"
          element={LazyComponent(BillingEarlyAccessPage)}
        />
        {/* GRW D21 — offers config, live-check receipts and the free-day list. */}
        <Route
          path="billing/offers"
          element={LazyComponent(BillingOffersPage)}
        />
        {/* Old bookmarks and any link still pointing into CRM. */}
        <Route
          path="crm/subscriptions"
          element={<Navigate to="/billing/subscribers" replace />}
        />

        {/* Future Admin Features */}
        <Route path="admin/users" element={LazyComponent(UsersManagement)} />
        {/* Admin-user management: roles, manager module grants, invitations. */}
        <Route
          path="admin/users/admins"
          element={LazyComponent(AdminUsersPage)}
        />
        {/* Subscriptions left CRM (ADM D1) — old bookmark redirects. */}
        <Route
          path="admin/subscriptions"
          element={<Navigate to="/billing/subscribers" replace />}
        />
        <Route path="admin/analytics" element={LazyComponent(Analytics)} />
        <Route path="admin/settings" element={LazyComponent(AdminSettings)} />

        {/* Account */}
        <Route path="logout" element={LazyComponent(LogoutComponent)} />
      </Route>

      {/* Catch all - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default Router;
